import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const FAQ_ITEMS = [
  {
    category: '工厂会员',
    icon: 'ri-building-line',
    color: 'text-red-500',
    bg: 'bg-red-50',
    q: '工厂会员前3个月免费是什么意思？',
    a: '注册工厂会员后，前3个月完全免费享受所有工厂会员权益，无需绑定任何支付方式。第4个月起自动转为¥5/月的付费计划，届时我们会提前通过邮件通知您，您可以随时取消。',
  },
  {
    category: '工厂会员',
    icon: 'ri-building-line',
    color: 'text-red-500',
    bg: 'bg-red-50',
    q: '工厂会员免费期结束后会自动扣费吗？',
    a: '不会自动扣费。免费期结束前7天，我们会发送提醒邮件。您需要主动选择续费方式才会产生费用。如果不续费，账号将自动降级为基础浏览权限，不会产生任何费用。',
  },
  {
    category: '分销商会员',
    icon: 'ri-share-line',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    q: '分销商会员为什么是永久免费的？',
    a: '分销商会员通过推广平台获得佣金收入，平台以此方式回馈推广伙伴。分销商会员资格永久免费，无任何隐藏费用。您只需成功推广用户注册，即可获得相应佣金。',
  },
  {
    category: '分销商会员',
    icon: 'ri-share-line',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    q: '分销商佣金是多少？如何结算？',
    a: '推广工厂会员获得月费5%佣金（约¥0.25/月），推广黄金会员获得月费30%佣金（约¥3/月）。佣金每月15日统一结算，通过PayPal或银行转账发放，最低结算金额¥50。',
  },
  {
    category: '分销商会员',
    icon: 'ri-share-line',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    q: '如何申请成为分销商？审核需要多久？',
    a: '在会员方案页面点击"申请分销商会员"，填写基本信息并同意协议即可提交申请。我们将在1-3个工作日内完成审核，审核通过后会通过邮件通知您，并开通专属分销后台。',
  },
  {
    category: '平台通用',
    icon: 'ri-question-line',
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    q: '工厂会员和分销商会员可以同时拥有吗？',
    a: '可以！工厂会员和分销商会员是两种不同的身份，可以同时拥有。工厂会员享受平台交友功能，分销商会员享受推广佣金权益。两者互不冲突，可以叠加使用。',
  },
  {
    category: '平台通用',
    icon: 'ri-question-line',
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    q: '黄金会员和工厂会员有什么区别？',
    a: '工厂会员（前3月免费，后¥5/月）提供基础交友功能，包括每日10次喜欢、基础搜索、每日5条消息。黄金会员（¥10/月）提供全部高级功能，包括无限消息、视频通话、置顶展示、专属情感顾问及全球800城市搜索。',
  },
];

export default function HomeFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('全部');
  const { ref, isVisible } = useScrollAnimation();

  const categories = ['全部', '工厂会员', '分销商会员', '平台通用'];
  const filtered = activeCategory === '全部' ? FAQ_ITEMS : FAQ_ITEMS.filter(f => f.category === activeCategory);

  return (
    <section ref={ref} className="py-20 bg-white">
      <div
        className="max-w-4xl mx-auto px-4 md:px-8 transition-all duration-700"
        style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-5 py-2 mb-4">
            <i className="ri-question-answer-line text-amber-500"></i>
            <span className="text-sm font-bold text-amber-700">常见问题</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            您最关心的问题
          </h2>
          <p className="text-gray-400 text-sm max-w-xl mx-auto">
            关于工厂会员免费试用、分销商计划及平台功能的常见疑问
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setOpenIdx(null); }}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="flex flex-col gap-3 mb-12">
          {filtered.map((item, i) => (
            <div
              key={i}
              className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-gray-200 transition-all"
            >
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full flex items-center gap-4 p-5 text-left cursor-pointer hover:bg-gray-50/50 transition-colors"
              >
                <div className={`w-8 h-8 flex items-center justify-center ${item.bg} rounded-lg flex-shrink-0`}>
                  <i className={`${item.icon} ${item.color} text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-400 mb-0.5">{item.category}</div>
                  <span className="text-sm font-semibold text-gray-800">{item.q}</span>
                </div>
                <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-300 flex-shrink-0 ml-2 ${openIdx === i ? 'rotate-180' : ''}`}></i>
              </button>
              {openIdx === i && (
                <div className="px-5 pb-5 pl-[72px]">
                  <p className="text-sm text-gray-500 leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Row */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 text-center">
          <h3 className="text-lg font-bold text-gray-800 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            还有其他问题？
          </h3>
          <p className="text-gray-400 text-sm mb-6">查看完整会员方案，或联系我们的客服团队</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/membership"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-vip-crown-line"></i>
              查看会员方案
            </Link>
            <Link
              to="/membership"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-emerald-500 text-emerald-600 rounded-full text-sm font-semibold hover:bg-emerald-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-line"></i>
              申请分销商（永久免费）
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
